import React, { useState } from 'react';

const ResponseCustomizer = () => {
  const [selectedPersona, setSelectedPersona] = useState('professional');
  const [customPrompt, setCustomPrompt] = useState('');
  const [testInput, setTestInput] = useState('');
  const [testOutput, setTestOutput] = useState('');

  const personas = [
    {
      id: 'professional',
      name: 'Professional',
      description: 'Formal and business-appropriate',
      prompt: 'Respond in a professional, formal manner suitable for business communication.',
      example: 'I\'ll be happy to assist you with that request. Let me gather the necessary information.'
    },
    {
      id: 'friendly',
      name: 'Friendly',
      description: 'Casual and approachable',
      prompt: 'Be friendly, casual, and approachable in your responses.',
      example: 'Hey there! Sure thing, let me help you out with that! 😊'
    },
    {
      id: 'technical',
      name: 'Technical',
      description: 'Detailed and precise',
      prompt: 'Provide technical, detailed responses with specific terminology.',
      example: 'Executing query on database index. Response payload: {status: 200, data: []}'
    },
    {
      id: 'concise',
      name: 'Concise',
      description: 'Brief and to the point',
      prompt: 'Be extremely concise. Use minimal words.',
      example: 'Done. Check #general.'
    },
    {
      id: 'creative',
      name: 'Creative',
      description: 'Fun and engaging',
      prompt: 'Be creative, use metaphors, and make responses engaging.',
      example: '🚀 Launching into action! Your request is like a rocket ready for liftoff!'
    }
  ];

  const responseSettings = [
    { key: 'maxTokens', label: 'Max Response Length', value: 500, type: 'number' },
    { key: 'temperature', label: 'Creativity Level', value: 0.7, type: 'range', min: 0, max: 1, step: 0.1 },
    { key: 'includeEmojis', label: 'Include Emojis', value: true, type: 'checkbox' },
    { key: 'includeCode', label: 'Format Code Blocks', value: true, type: 'checkbox' },
    { key: 'threadAware', label: 'Thread Context Aware', value: true, type: 'checkbox' },
    { key: 'mentionUsers', label: 'Mention Users', value: true, type: 'checkbox' }
  ];

  const contextRules = [
    {
      channel: '#general',
      rules: ['Keep responses family-friendly', 'No technical jargon', 'Be inclusive']
    },
    {
      channel: '#dev-team',
      rules: ['Use technical terms', 'Include code examples', 'Reference documentation']
    },
    {
      channel: '#trading',
      rules: ['Include market data', 'Be precise with numbers', 'Add disclaimers']
    },
    {
      channel: '#support',
      rules: ['Be empathetic', 'Provide clear solutions', 'Follow up on issues']
    }
  ];

  const testResponse = () => {
    const persona = personas.find(p => p.id === selectedPersona);
    if (persona && testInput) {
      setTestOutput(`[${persona.name} Mode]\n\n${persona.example}\n\nProcessing: "${testInput}"`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Persona Selection */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Response Personas</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {personas.map((persona) => (
            <button
              key={persona.id}
              onClick={() => setSelectedPersona(persona.id)}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedPersona === persona.id
                  ? 'bg-green-900 border-green-400'
                  : 'bg-black border-gray-600 hover:border-gray-500'
              }`}
            >
              <div className="font-bold text-yellow-400 mb-1">{persona.name}</div>
              <div className="text-xs text-gray-400 mb-2">{persona.description}</div>
              <div className="text-xs text-gray-500 italic">"{persona.example}"</div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Prompt Configuration */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Custom System Prompt</h3>
        <textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder={personas.find(p => p.id === selectedPersona)?.prompt}
          className="w-full h-32 bg-black text-green-400 p-3 rounded border border-gray-600 focus:border-green-400 focus:outline-none font-mono text-sm"
        />
        <p className="text-xs text-gray-500 mt-2">
          This prompt will be used to customize Claude's response style
        </p>
      </div>

      {/* Response Settings */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Response Settings</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {responseSettings.map((setting) => (
            <div key={setting.key} className="flex items-center justify-between bg-black p-3 rounded">
              <label className="text-sm text-gray-400">{setting.label}</label>
              {setting.type === 'checkbox' ? (
                <input
                  type="checkbox"
                  defaultChecked={setting.value as boolean}
                  className="w-4 h-4 text-green-600 bg-gray-900 border-gray-600 rounded focus:ring-green-500"
                />
              ) : setting.type === 'range' ? (
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    defaultValue={setting.value as number}
                    min={setting.min}
                    max={setting.max}
                    step={setting.step}
                    className="w-24"
                  />
                  <span className="text-xs text-gray-500 w-8">{setting.value}</span>
                </div>
              ) : (
                <input
                  type={setting.type}
                  defaultValue={setting.value as number}
                  className="bg-gray-900 text-green-400 px-2 py-1 rounded border border-gray-600 w-20 text-sm"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Channel-Specific Rules */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Channel-Specific Rules</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {contextRules.map((context) => (
            <div key={context.channel} className="bg-black p-4 rounded border border-gray-600">
              <div className="font-bold text-yellow-400 mb-2">{context.channel}</div>
              <ul className="space-y-1">
                {context.rules.map((rule, idx) => (
                  <li key={idx} className="text-sm text-gray-400 flex items-start">
                    <span className="text-green-400 mr-2">•</span>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Test Response */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Test Response</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Test Input
            </label>
            <input
              type="text"
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              placeholder="Enter a test message..."
              className="w-full bg-black text-green-400 p-3 rounded border border-gray-600 focus:border-green-400 focus:outline-none"
            />
          </div>
          <button
            onClick={testResponse}
            className="px-6 py-2 bg-green-600 text-black rounded-lg hover:bg-green-500 transition-colors font-bold"
          >
            Generate Test Response
          </button>
          {testOutput && (
            <div className="bg-black p-4 rounded border border-green-400">
              <pre className="text-sm text-gray-300 whitespace-pre-wrap">{testOutput}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResponseCustomizer;